/**
 * @date: 1/17/21
 * @author: Yan Wang <ywang@max.md>
 * @copy: (C) Copyright 2021 MaxMD Corporation, All Rights Reserved.
 *
 */
import React, {useState, useEffect} from 'react';
import clsx from 'clsx';
import makeStyles from '@mui/styles/makeStyles';
import CssBaseline from '@mui/material/CssBaseline';
import Drawer from '@mui/material/Drawer';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import {LockOpen, Gesture, Description, Pageview, Home} from "@mui/icons-material";
import Badge from '@mui/material/Badge'

import './documentation.css';
import {CircularProgress, ListSubheader} from "@mui/material";
import Backdrop from "@mui/material/Backdrop";
import ShowMessage from "../utils/show-message";
import Copyright from "../utils/Copyright";
import LibraryAddCheckIcon from '@mui/icons-material/LibraryAddCheck';
import DnsOutlinedIcon from '@mui/icons-material/DnsOutlined';
import ListItemText from "@mui/material/ListItemText";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import {withStyles} from "@mui/styles";
import styled from "@emotion/styled";

const drawerWidth = 300;

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
    },
    toolbar: {
        paddingRight: 24, // keep right padding when drawer closed
    },
    toolbarIcon: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        padding: '0 8px',
        ...theme.mixins.toolbar,
    },
    appBar: {
        zIndex: theme.zIndex.drawer + 1,
        transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        }),
    },
    appBarShift: {
        marginLeft: drawerWidth,
        width: `calc(100% - ${drawerWidth}px)`,
        transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
    },
    menuButton: {
        marginRight: 36,
    },
    menuButtonHidden: {
        display: 'none',
    },
    title: {
        flexGrow: 1,
    },
    drawerPaper: {
        position: 'relative',
        whiteSpace: 'nowrap',
        width: drawerWidth,
        transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
    },
    drawerPaperClose: {
        overflowX: 'hidden',
        transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        }),
        width: 0,
        [theme.breakpoints.up('sm')]: {
            width: theme.spacing(9),
        },
    },
    appBarSpacer: theme.mixins.toolbar,
    content: {
        flexGrow: 1,
        height: '100vh',
        overflow: 'auto',
    },
    container: {
        paddingTop: theme.spacing(4),
        paddingBottom: theme.spacing(4),
    },
    paper: {
        padding: theme.spacing(2),
        display: 'flex',
        overflow: 'auto',
        flexDirection: 'column',
    },
    fixedHeight: {
        height: 240,
    },
    backdrop: {
        zIndex: theme.zIndex.drawer + 1,
        color: '#fff'
    },
    link: {
        color: 'white',
    },
    copyRight: {
        position: 'fixed',
        bottom: 10, left: 50
    },
    logoWrapper: {
        '& img' : {width: 155, marginRight: 32}
    },
    toolbarActionWrapper: {
        position: "absolute",
        right: 10
    },
    full_width: {
        maxWidth: '100%'
    },

}));

const MenuItemBadge = withStyles((theme) => ({
    badge: {
        right: -6,
        top: -10,
        padding: '0 4px',
    },
}))(Badge);



export default function DocumentationHome(props) {
    console.log('DocumentationHome', props);
    const classes = useStyles();

    const [msg, setMsg] = useState({});
    const [showBatchBar, setShowBatchBar] = useState(false);

    const [open, setOpen] = useState(true);
    const handleDrawerOpen = () => {
        setOpen(true);
    };
    const handleDrawerClose = () => {
        setOpen(false);
    };

    const [loading, setLoading] = useState(false);
    const [activeBatchProcess, setActiveBatchProcess] = useState(null);
    const [contextIgTransactions, setContextIgTransactions] = useState([]);

    // Set these values for the current environment
    const MAXSIG_ENDPOINT = "http://" + window.location.hostname;

    // JSON examples for interactions
    let pdfSignSchema = {
        "documentName": "String: file name of the PDF",
        "widget": {
            "x": "Int: x position of signature area (in pixels)",
            "y": "Int: y positioning of signature area (in pixels)",
            "width": "Int: width of signature area (in pixels)",
            "height": "Int: height of signature area (in pixels)",
            "page": "Int: number of pages in the PDF",
            "pageHeight": "Int: height of each page (in pixels)"
        },
        "reason": "String: digital signature reason",
        "data": "String: base64 encoded value of the PDF to sign",
        "graphicalSignature": "String: base64 encoded value of the graphical signature image"
    }
    let pdfSignReturn = {
        "documentId": "String: internal document ID",
        "documentName": "String: file name of the pdf",
        "documentContentType": "String fixed to application/pdf",
        "documentContent": "String: base64 encoded signed PDF"
    }
    let cdaSignSchema = {
        "documentName": "String: file name of the CDA",
        "data": "String: base64 encoded value of the CDA to sign"
}
    let cdaSignReturn = {
        "documentId": "String: internal document ID",
        "documentName": "String: file name of the pdf",
        "documentContentType": "String fixed to text/xml",
        "documentContent": "String: base64 encoded signed CDA"
    }
    let pdfVerifySchema = "String: base64 encoded signed PDF. Can be the documentContent value returned from the pdfSign interaction.";
    let pdfVerifyReturn = {
        "description": "String: digital signatures description",
        "documentDigest": "String: document digest",
        "identifier": "String: internal document ID",
        "documentType": "String fixed to PDF",
        "createdTime": "Date: signature creation timestamp",
        "pages": [
            {
                "index": "Int: page number. Starts at 1",
                "width": "Float: width of the pdf page (in pixels)",
                "height": "Float: height of the pdf page (in pixels)",
                "rotate": "Int: degree rotation of the current page"
            }
        ],
        "metaData": {
            "CreationDate": "Date: timestamp of when the original pdf was created",
            "Producer": "String: the application that converted the base document to the original unsigned pdf",
            "Title": "String: metadata title. Set when creating the original pdf and not necessarily the pdf file name",
            "Creator": "String: the application that was used to write the base document before being converted to a pdf",
            "ModDate": "Date: timestamp of when the original pdf was last modified"
        },
        "signatures": [
            {
                "pageIndex": "Int: page number. Starts at 1",
                "left": "Int: left margin of signature area (in pixels)",
                "top": "Int: top margin of signature area (in pixels)",
                "width": "Int: width of signature area (in pixels)",
                "height": "Int: height of signature area (in pixels)",
                "name": "String: signature field name, used as signature id",
                "reason": "String: digital signature reason",
                "signatureTime": "Date: timestamp of when this signature was completed",
                "signer": "String: signer name",
                "signerCn": "String: signer certificate Common Name (CN)",
                "signerEmail": "String signer email",
                "signerSubject": "String: signer certificate subject",
                "status": "String: signature validity",
                "issigned": "Int: 0 or 1 representing signature area completed status"
            }
        ],
        "formFields": {
            "tabIndex": "Int: page number of this pdf form field. Starts at 1",
            "states": "String: used if this field is a form checkbox or radio button. Indicates if the field is on or off. On values could be an arbitrary string. Off values will always be Off",
            "options": "String[]: holds option values of radio button checklist",
            "type": "String: form field type (i.e., text field, radio group, checkbox, list, combo)",
            "checkValue": "String: used if this field is a checkbox or radio button and is on. Holds the on value of the button",
            "uncheckValue": "String: used if this field is a checkbox or radio button and is off. Holds the off value of the button",
            "name": "String: form field name",
            "value": "String: form field value",
            "format": "String: form field format (i.e., date, number, normal)",
            "required": "Boolean: indicates if this pdf form field must be filled",
            "checked": "Int: used if this field is a checkbox or radio button. Indicates if the button is on or off",
            "readonly": "Boolean: indicates if this pdf form field cannot be modified",
        }
    }
    let cdaVerifySchema = "String: base64 encoded signed CDA. Can be the documentContent value returned from the cdaSign interaction.";
    let cdaVerifyReturn = {
        "description": "String: digital signatures description",
        "documentDigest": "String: document digest",
        "identifier": "String: internal document ID",
        "documentType": "String fixed to CDA",
        "createdTime": "Date: signature creation timestamp",
        "signatures": [
            {
                "xAdESSignature": {
                    "valid": "Boolean: signature validity",
                    "signatureLocation": "String: path to signature tag in CDA",
                    "verificationError": "String: errors encountered during verification",
                    "formType": "String: xAdESSignature form type",
                    "validationData": {
                        "certs": [
                            "String[]: list of X.509 certs"
                        ],
                        "crls": [
                            "String[]: list of X.509 Certificate Revocation List (CRL). A CRL is a time-stamped list identifying revoked certificates"
                        ]
                    }
                },
                "signatureTextThumbnail": "String: description of signer including signer, signer email, and signature timestamp",
                "authenticatorTime": "Date: timestamp of when this signature was completed",
                "authenticatorSignatureCode": "String: signature code",
                "authenticatorAssignedPersonName": "String: signer name"
            }
        ]
    }


    return (
        <div className={`${classes.root} dashboard-wrapper header-wrapper`}>
            <CssBaseline />
            <AppBar position="absolute" className={clsx(classes.appBar, open && classes.appBarShift)}>
                <Toolbar className={`${classes.toolbar} 'header-toolbar'}`}>
                    <IconButton
                        edge="start"
                        color="inherit"
                        aria-label="open drawer"
                        onClick={handleDrawerOpen}
                        className={clsx(classes.menuButton, open && classes.menuButtonHidden)}
                        size="large">
                        <MenuIcon />
                    </IconButton>
                    {/*<div className='logo-wrapper'>*/}
                    {/*    <img src={require('../images/dt-logo.png')} height={40} />*/}
                    {/*</div>*/}
                    <div className='prod-name'>
                        <span >MaxSignatures API Documentation</span>
                    </div>

                    <div className={classes.toolbarActionWrapper} >
                        {contextIgTransactions && contextIgTransactions.length > 0 && <IconButton
                            color="inherit"
                            className={classes.portalStatusButton}
                            onClick={() => setShowContextTransactioinDialog(true)}
                            size="large">
                            <LibraryAddCheckIcon/> <MenuItemBadge badgeContent={contextIgTransactions.length} color="secondary" className={classes.portalStatusButtonBadge}/>
                        </IconButton> }

                        {activeBatchProcess && <IconButton
                            color="inherit"
                            className={classes.portalStatusButton}
                            onClick={() => setShowBatchBar(!showBatchBar)}
                            size="large">
                            <DnsOutlinedIcon />
                            <MenuItemBadge badgeContent={1} color="secondary" className={classes.portalStatusButtonBadge}/>
                        </IconButton>}
                    </div>


                </Toolbar>
            </AppBar>


            <Drawer
                variant="permanent" width={drawerWidth}
                classes={{
                    paper: clsx(classes.drawerPaper, !open && classes.drawerPaperClose),
                }}
                open={open}
            >
                <div className={classes.toolbarIcon}>
                    <div className={classes.logoWrapper} >
                        <img src={require('../images/dt-logo.png')?.default}/>
                    </div>
                    <IconButton onClick={handleDrawerClose} size="large">
                        <ChevronLeftIcon />
                    </IconButton>
                </div>
                <Divider />
                <List>
                    <a className="drawerLink" href="..">
                        <ListItem button>
                            <ListItemIcon>
                                <Home />
                            </ListItemIcon>
                            <ListItemText primary="Home Page" />
                        </ListItem>
                    </a>
                    <a className="drawerLink" href="#about_section">
                        <ListItem button>
                            <ListItemIcon>
                                <Description />
                            </ListItemIcon>
                            <ListItemText primary="About" />
                        </ListItem>
                    </a>
                    <a className="drawerLink" href="#authentication_section">
                        <ListItem button>
                            <ListItemIcon>
                                <LockOpen />
                            </ListItemIcon>
                            <ListItemText primary="Authentication" />
                        </ListItem>
                    </a>
                    <a className="drawerLink" href="#interactions_section">
                    <ListItem button>
                        <ListItemIcon>
                            <Gesture/>
                        </ListItemIcon>
                        <ListItemText primary="Interactions"/>
                    </ListItem>
                </a>
                    <a className="drawerLink" href="#examples_section">
                        <ListItem button>
                            <ListItemIcon>
                                <Pageview/>
                            </ListItemIcon>
                            <ListItemText primary="Examples"/>
                        </ListItem>
                    </a>
                </List>
                {/*<Divider />*/}
                <div className={classes.copyRight} >
                    <Copyright />
                </div>
            </Drawer>


            <main className={classes.content}>
                <div className={classes.appBarSpacer}/>

                <ShowMessage msg={msg}/>

                <br/>

                <div id="mainBody" className={classes.full_width}>
                    {/*Overview section*/}
                    <div id="about_section" className="doc_section">
                        <h2 id="about">About MaxSignature API</h2>
                        <p>The MaxSignatures API creates unalterable documents and forms with legally enforceable digital signatures. The MaxSignatures API supports digitally signing PDFs and CDAs as well as verifying signed PDFs and CDAs.</p>
                        <p>For more information on the MaxSignatures service in general, visit <a href="https://www.maxmddirect.com/info/products-services/MaxSignatures" target="_blank">our product page.</a></p>
                    </div>
                    <hr />

                    {/*Authentication section*/}
                    <div id="authentication_section" className="doc_section">
                        <h2 id="authentication">Authentication</h2>
                        <p>All interactions with the MaxSignatures API require two Authentication values in each Request header:</p>
                        <table>
                            <tbody>
                                <tr>
                                    <th>Token</th>
                                    <th>Type</th>
                                    <th>Value</th>
                                </tr>
                                <tr>
                                    <td>MaxMD User Credentials</td>
                                    <td>Basic</td>
                                    <td>base64 encoded username:password</td>
                                </tr>
                                <tr>
                                    <td>ID.me id_token</td>
                                    <td>Bearer</td>
                                    <td>3 part jwt returned from ID.me login</td>
                                </tr>
                            </tbody>
                        </table>
                        <h4 className="page-header">1. Basic Token - MaxMD User Credentials</h4>
                        <p>The MaxMD User Credentials are provided when registering for this service by contacting <a href="mailto:support@max.md">support@max.md</a>. Users must create a <a href="https://en.wikipedia.org/wiki/Basic_access_authentication" target="_blank">HTTP Basic authentication token</a> from these credentials.</p>
                        <p>For example, a user that has an account with credentials "username1" "password2" must base64 encode "username1:password2" to get the encoded value "dXNlcm5hbWUxOnBhc3N3b3JkMg==".</p>
                        <p>This user will include "Basic dXNlcm5hbWUxOnBhc3N3b3JkMg==" as part of the Authorization header in every API request.
                        </p>
                        <h4 className="page-header">2. Bearer Token - ID.me verification id_token</h4>
                        <p>Every API call also requires a second value from ID.me's IAL2 verification process. MaxSignatures API Users <strong>must integrate <a href="https://developers.id.me/" target="_blank">ID.me's verification interface</a> to retrieve ID.me's id_token.</strong></p>
                        <p>Unlike the MaxMD User Credentials Authorization token, the ID.me token expires after a period of time and users must retrieve a new one. Simply go through ID.me's verification process and you will be provided an id_token field upon successful verification. The below image is an example callback page a user may set up for their ID.me interface:</p>
                        <img src={require('../images/id_token_example.png')?.default} width="90%" style={{marginLeft: '5%',marginRight: '5%'}}/>
                        <p>The id_token is a JWT. Parsing the JWT will show you some info about the id_token such as the expiration timestamp, signer information, etc.</p>
                        <img src={require('../images/jwt.png')?.default} style={{margin: 'auto', maxWidth: '90%', maxHeight: '400px'}}/>
                        <p>This id_token will act as your Bearer Authorization token. This user will include "Bearer eyJ0eXAi...HeeRA" as part of the Authorization header in every API request until the id_token value expires.</p>
                        <h4 className="page-header">Authentication Overview</h4>
                        <p>
                            To summarize, all MaxSignatures API calls require two Authorization tokens. A Basic token for base64 encoded MaxMD User Credentials and a Bearer token for the id_token returned from verifying through ID.me. These two values must both be included in every API Request Header. They can be written as one line separated by a comma:
                        </p>
                        <p>
                            "Basic dXNlcm5hbWUxOnBhc3N3b3JkMg==, Bearer eyJ0eXAi...HeeRA"
                        </p>
                    </div>
                    <hr />

                    {/*Interactions Section*/}
                    <div id="interactions_section" className="doc_section">
                        <h2 id="interactions">Interactions</h2>
                        <p>MaxSignatures API interactions are simple to create and fast to execute. The API currently supports 4 functionalities:</p>
                        <ol>
                            <li>Digitally signing a PDF document</li>
                            <li>Digitally signing a CDA document</li>
                            <li>Verifying a digitally signed PDF document</li>
                            <li>Verifying a digitally signed CDA document</li>
                        </ol>
                        <p>Each interaction requires forming one HTTP request. The following subsections will specify how to initiate each interaction.</p>

                        {/*Signing Sub-section*/}
                        <div id="func_pdf_sign" className="func">
                            <h4 className="page-header">1. Digitally signing a PDF document</h4>
                            <p>Use this interaction to digitally sign a PDF document. The request must include the PDF document to sign,
                                the graphical signature image to be added to the document, placement info for the signature,
                                etc. See the Example Request Body for a full schema.</p>
                            <p>To start this interaction, create a HTTP <span className="http-post">POST</span> Request to <span className="endpoint_text">{MAXSIG_ENDPOINT}/sign/pdf</span></p>
                            <p>The Request must include the following two headers:</p>
                            <table>
                                <tbody>
                                    <tr>
                                        <th>Key</th>
                                        <th>Value</th>
                                    </tr>
                                    <tr>
                                        <td>Content-Type</td>
                                        <td>application/json</td>
                                    </tr>
                                    <tr>
                                        <td><a href="#authentication_section">Authorization</a></td>
                                        <td>Basic &#123;MaxMD User Credentials&#125;, Bearer &#123;ID.me id_token&#125;</td>
                                    </tr>
                                </tbody>
                            </table>
                            <p><strong>PDF Sign Request Body Schema</strong></p>
                            <textarea id="pdf_sign_schema_area" className="schema_area tall_area" readOnly={true} value={JSON.stringify(pdfSignSchema, undefined, 4)}/>
                            <p><strong>PDF Sign Response Schema</strong></p>
                            <textarea id="pdf_sign_return_area" className="schema_area short_area" readOnly={true} value={JSON.stringify(pdfSignReturn, undefined, 4)}/>
                        </div>

                        <div id="func_cda_sign" className="func">
                            <h4 className="page-header">2. Digitally signing a CDA document</h4>
                            <p>Use this interaction to digitally sign a CDA document. The request must include the CDA document to sign. See the Example Request Body for a full schema.</p>
                            <p>To start this interaction, create a HTTP <span className="http-post">POST</span> Request to <span className="endpoint_text">{MAXSIG_ENDPOINT}/sign/cda</span></p>
                            <p>The Request must include the following two headers:</p>
                            <table>
                                <tbody>
                                <tr>
                                    <th>Key</th>
                                    <th>Value</th>
                                </tr>
                                <tr>
                                    <td>Content-Type</td>
                                    <td>application/json</td>
                                </tr>
                                <tr>
                                    <td><a href="#authentication_section">Authorization</a></td>
                                    <td>Basic &#123;MaxMD User Credentials&#125;, Bearer &#123;ID.me id_token&#125;</td>
                                </tr>
                                </tbody>
                            </table>
                            <p><strong>CDA Sign Request Body Schema</strong></p>
                            <textarea id="cda_sign_schema_area" className="schema_area short_area" readOnly={true} value={JSON.stringify(cdaSignSchema, undefined, 4)}/>
                            <p><strong>CDA Sign Response Schema</strong></p>
                            <textarea id="cda_sign_return_area" className="schema_area short_area" readOnly={true} value={JSON.stringify(cdaSignReturn, undefined, 4)}/>
                        </div>

                        <div id="func_pdf_verify" className="func">
                            <h4 className="page-header">3. Verifying a digitally signed PDF document</h4>
                            <p>Use this interaction to verify the signature on a digitally signed PDF. The request body is the base64 encoded signed PDF to verify.</p>
                            <p>To start this interaction, create a HTTP <span className="http-post">POST</span> Request to <span className="endpoint_text">{MAXSIG_ENDPOINT}/verify/pdf</span></p>
                            <p>The Request must include the following two headers:</p>
                            <table>
                                <tbody>
                                <tr>
                                    <th>Key</th>
                                    <th>Value</th>
                                </tr>
                                <tr>
                                    <td>Content-Type</td>
                                    <td>text/plain</td>
                                </tr>
                                <tr>
                                    <td><a href="#authentication_section">Authorization</a></td>
                                    <td>Basic &#123;MaxMD User Credentials&#125;, Bearer &#123;ID.me id_token&#125;</td>
                                </tr>
                                </tbody>
                            </table>
                            <p><strong>PDF Verify Request Body</strong></p>
                            <textarea id="pdf_verify_schema_area" className="schema_area short_area" readOnly={true} value={pdfVerifySchema}/>
                            <p><strong>PDF Verify Response Schema</strong></p>
                            <textarea id="pdf_verify_return_area" className="schema_area xl_tall_area" readOnly={true} value={JSON.stringify(pdfVerifyReturn, undefined, 4)}/>
                        </div>

                        <div id="func_cda_verify" className="func">
                            <h4 className="page-header">4. Verifying a digitally signed CDA document</h4>
                            <p>Use this interaction to verify the signature on a digitally signed CDA. The request body is the base64 encoded signed CDA to verify.</p>
                            <p>To start this interaction, create a HTTP <span className="http-post">POST</span> Request to <span className="endpoint_text">{MAXSIG_ENDPOINT}/verify/cda/encoded</span></p>
                            <p>The Request must include the following two headers:</p>
                            <table>
                                <tbody>
                                <tr>
                                    <th>Key</th>
                                    <th>Value</th>
                                </tr>
                                <tr>
                                    <td>Content-Type</td>
                                    <td>text/plain</td>
                                </tr>
                                <tr>
                                    <td><a href="#authentication-section">Authorization</a></td>
                                    <td>Basic &#123;MaxMD User Credentials&#125;, Bearer &#123;ID.me id_token&#125;</td>
                                </tr>
                                </tbody>
                            </table>
                            <p><strong>CDA Verify Request Body</strong></p>
                            <textarea id="cda_verify_schema_area" className="schema_area short_area" readOnly={true} value={cdaVerifySchema}/>
                            <p><strong>CDA Verify Response Schema</strong></p>
                            <textarea id="cda_verify_return_area" className="schema_area xl_tall_area" readOnly={true} value={JSON.stringify(cdaVerifyReturn, undefined, 4)}/>
                        </div>
                    </div>
                    <hr />

                    {/*Examples section*/}
                    <div id="examples_section" className="doc_section">
                        <h2 id="about">Example Interactions</h2>
                        <p>The following sections will provide an example user scenario. The user retrieves an ID.me id_token. They then use this token to digitally sign a sample pdf. Lastly, they verify the contents of the signed pdf. The examples use Postman to generate API calls, but users may choose any method to interact with the API (e.g., curl, Postman, automated program, etc.)</p>

                        <div id="idme_token_example" className="func">
                            <h4 className="page-header">1. Retrieving an ID.me id_token</h4>
                            <ol>
                                <li>
                                    User navigates to their internal implementation of the ID.me login interface.
                                    <img src={require('../images/idme_interface.png')?.default} className="example_img" />
                                </li>
                                <li>
                                    User goes through ID.me's verification process.
                                </li>
                                <li>
                                    User is provided their id_token upon success. In this example, the user is redirected to a callback page.
                                    <img src={require('../images/id_token_example.png')?.default} className="example_img" />
                                </li>
                            </ol>
                        </div>

                        <div id="pdf_sign_example" className="func">
                            <h4 className="page-header">2. Digitally signing a sample PDF</h4>
                            <ol>
                                <li>
                                    User creates a Request to the PDF sign endpoint. User adds in the required Headers.
                                    <img src={require('../images/sign_header.png')?.default} className="example_img" />
                                </li>
                                <li>
                                    User fills in the Request body based on the schema defined for the interaction.
                                    <img src={require('../images/sign_body.png')?.default} className="example_img" />
                                </li>
                                <li>
                                    User is returned the interaction Response. The user may decode the documentContent value to see their digitally signed PDF.
                                    <img src={require('../images/sign_response.png')?.default} className="example_img" />
                                </li>
                            </ol>
                        </div>

                        <div id="pdf_verify_example" className="func">
                            <h4 className="page-header">3. Verifying a digitally signed PDF</h4>
                            <ol>
                                <li>
                                    User creates a Request to the verify signed PDF endpoint. User adds in the required Headers.
                                </li>
                                <li>
                                    User fills in the Request body based on the schema defined for the interaction. The Request body is the value in the documentContent field of the previous interaction.
                                </li>
                                <li>
                                    User is returned info about the digital signature on the document.
                                    <img src={require('../images/verify_response.png')?.default} className="example_img" />
                                </li>
                            </ol>
                        </div>
                    </div>
                </div>

            </main>

            <Backdrop className={classes.backdrop} open={loading} >
                <CircularProgress color={"inherit"}/>
            </Backdrop>
        </div>
    );
}

